/**
 * Session expirée : point de rencontre entre le client HTTP et l'application.
 *
 * POURQUOI CE MODULE EXISTE : `apiRequest` n'a pas de routeur ni de store — il
 * ne peut donc ni rediriger ni prévenir l'utilisateur. Sans lui, un 401 dû à
 * une session morte remontait tel quel à l'appelant, qui affichait le message
 * brut du serveur au bout de son propre écran : « Identifiants invalides »
 * sous un envoi de demande de devis, alors que personne n'avait saisi
 * d'identifiant. Le client signale ici, l'application (App.vue) écoute et
 * décide quoi faire.
 *
 * La détection se fait sur le STATUT 401, jamais sur le texte du message : le
 * libellé côté serveur peut changer sans casser ce chemin.
 */

let handler = null

/**
 * Chemins d'API dont un 401 n'est PAS une session expirée :
 *  - `/auth/…` : connexion (401 = mauvais identifiants), déconnexion, et les
 *    sondes `…/auth/me` que les gardes de navigation interrogent justement
 *    pour SAVOIR s'il y a une session — les rediriger ici ferait doublon.
 *  - `/api/public/…` : jamais authentifié.
 *  - `/api/user-preferences/…` : les préférences du COMPTE DE BACK-OFFICE
 *    (réglages de listes). Elles
 *    partent en arrière-plan au démarrage de l'application, AVANT même que le
 *    routeur n'ait résolu la navigation initiale, et sans session de
 *    back-office la réponse est 401 par construction : c'est le cas de tout
 *    visiteur d'une page publique, de tout formateur dans son portail, et de
 *    toute adresse inconnue. Ce 401-là faisait passer pour une expiration ce
 *    qui n'est qu'une ABSENCE DE COMPTE back-office, et expédiait vers la
 *    connexion des gens qui ouvraient un favori de leur espace, une rubrique
 *    déplacée ou la page introuvable — écrans qu'ils n'atteignaient donc
 *    jamais. Ces lectures avalent déjà ce 401 en silence
 *    (`useListPreferences`) et retombent sur leur défaut : personne n'attend
 *    de message ici.
 */
function isSessionProbeOrPublic(url) {
  return url.includes('/auth/')
    || url.includes('/api/public/')
    || url.includes('/api/user-preferences')
}

/**
 * Enregistre le comportement applicatif (prévenir + rediriger).
 * Appelé une fois au démarrage depuis App.vue.
 */
export function setSessionExpiredHandler(fn) {
  handler = fn
}

/** Test only : remet le module à son état initial. */
export function resetSessionExpiredHandler() {
  handler = null
}

/**
 * Signale un 401 reçu sur `url`. Sans conséquence si l'URL est une sonde de
 * session, une route publique, ou si aucun handler n'est enregistré (embed,
 * tests unitaires).
 */
export function notifySessionExpired(url) {
  if (!handler || typeof url !== 'string' || isSessionProbeOrPublic(url)) return
  handler(url)
}
